import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Archive, Briefcase, Eye, FileStack, Plus, RotateCcw, Search, Sparkles, Target, Users } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";

const PLAN_SCAN_LIMITS = {
  free: 5,
  basic: 200,
  pro: 500,
  pilot: 75,
};

export default function Jobs() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [jobView, setJobView] = useState("active");
  const [updatingJobId, setUpdatingJobId] = useState(null);
  const [createdJobNotice, setCreatedJobNotice] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  const userPlan = user?.plan || "free";
  const hasPaidPlan = userPlan === "basic" || userPlan === "pro" || userPlan === "pilot" || userPlan === "enterprise";
  const planLimit = PLAN_SCAN_LIMITS[userPlan] ?? PLAN_SCAN_LIMITS.free;
  const scansLeft = Math.max(0, planLimit - (user?.used_scans || 0));
  const quotaExceeded = scansLeft <= 0;
  const bulkScanLocked = !hasPaidPlan || quotaExceeded;

  useLongActionNotice(creating, "Creating the job", 30000);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchJobs = async () => {
    setFetching(true);

    try {
      const endpoint = jobView === "archived" ? "/jobs?archived_only=true" : "/jobs";
      const res = await apiFetch(endpoint);
      const data = await res.json();

      if (!res.ok) {
        toast.error(data?.detail || "Failed to load jobs.");
        return;
      }

      setJobs(data || []);
    } catch (err) {
      toast.error("Failed to load jobs.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchJobs();
  }, [user, jobView]);

  const filteredJobs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return jobs;

    // This is a local filter over the already-loaded job list. Candidate-level
    // semantic search remains in the existing Candidate Search page.
    return jobs.filter((job) => {
      const text = `${job.name || ""} ${job.description || ""}`.toLowerCase();
      return text.includes(normalizedQuery);
    });
  }, [jobs, query]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateJob = async (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const description = form.description.trim();

    if (!name || !description) {
      toast.error("Job title and job description are required.");
      return;
    }

    setCreating(true);

    try {
      const res = await apiFetch("/jobs", {
        method: "POST",
        body: JSON.stringify({ name, description }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Job creation", data?.detail));
        return;
      }

      setJobs((prev) => [data, ...prev]);
      setForm({ name: "", description: "" });
      setCreatedJobNotice(data);
      toast.success("Job created. You can now add candidates to this job.");
    } catch (err) {
      toast.error(actionFailureMessage("Job creation"));
    } finally {
      setCreating(false);
    }
  };

  const handleArchiveJob = async (jobId) => {
    if (!window.confirm("Archive this job? It will be removed from active job lists, but its history will be kept.")) {
      return;
    }

    setUpdatingJobId(jobId);

    try {
      const res = await apiFetch(`/jobs/${jobId}/archive`, { method: "PATCH" });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Job archive", data?.detail));
        return;
      }

      setJobs((prev) => prev.filter((job) => job.id !== jobId));
      toast.success("Job archived.");
    } catch (err) {
      toast.error(actionFailureMessage("Job archive"));
    } finally {
      setUpdatingJobId(null);
    }
  };

  const handleRestoreJob = async (jobId) => {
    setUpdatingJobId(jobId);

    try {
      const res = await apiFetch(`/jobs/${jobId}/restore`, { method: "PATCH" });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Job restore", data?.detail));
        return;
      }

      setJobs((prev) => prev.filter((job) => job.id !== jobId));
      toast.success("Job restored.");
    } catch (err) {
      toast.error(actionFailureMessage("Job restore"));
    } finally {
      setUpdatingJobId(null);
    }
  };

  const handleBulkScanClick = (jobId) => {
    if (bulkScanLocked) {
      toast.error(
        !hasPaidPlan
          ? "Bulk Scan is available on Basic and Pro plans."
          : "You have no scans left in this billing cycle."
      );
      return;
    }

    navigate(`/bulk-scan?job_id=${jobId}`);
  };

  const handleAddCandidatesClick = (jobId) => {
    if (!hasPaidPlan) {
      toast.error("Candidate import is available on Basic and Pro plans.");
      return;
    }

    navigate(`/candidates?job_id=${jobId}&scope=job`);
  };

  const handleFindExistingMatchesClick = (jobId) => {
    if (!hasPaidPlan) {
      toast.error("Candidate Re-discovery is available on Basic and Pro plans.");
      return;
    }

    navigate(`/jobs/${jobId}?focus=rediscovery`);
  };

  const handleCriteriaClick = (job) => {
    if ((job.scan_count || 0) <= 0) {
      toast.error("Run a scan for this job first. Criteria Simulator becomes available after scanned candidate results exist.");
      return;
    }

    navigate(`/jobs/${job.id}/criteria-simulator`);
  };

  const leftPanel = <RecruiterSidebar activeItem="jobs" user={user} scansLeft={scansLeft} />;

  const mainContent = (
    <>
      <section className="overflow-hidden rounded-lg border border-emerald-900/20 bg-[#031b14] shadow-sm">
        <div className="p-5 text-white">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
              <Briefcase size={14} />
              Job workspace
            </div>
            <h2 className="mt-4 text-xl font-semibold">Create Job</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-50/80">
              Create the role first, then scan CVs or review candidates from that job.
            </p>
          </div>
        </div>

        <form onSubmit={handleCreateJob} className="space-y-4 border-t border-white/10 bg-white p-5 text-slate-900">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Job title</span>
            <input
              name="name"
              value={form.name}
              onChange={handleFormChange}
              placeholder="Senior Backend Engineer"
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Job description</span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleFormChange}
              rows={6}
              placeholder="Paste the role requirements, skills, responsibilities, and experience expectations."
              className="mt-2 w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            />
          </label>

          <button
            type="submit"
            disabled={creating}
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:opacity-50"
          >
            <Plus size={16} />
            {creating ? "Creating..." : "Create Job"}
          </button>
        </form>
      </section>

      {createdJobNotice && (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-emerald-950">Job created successfully</p>
              <p className="mt-1 text-sm leading-6 text-emerald-800">
                {createdJobNotice.name} is ready. Add new applicants or check your saved pool for existing matches.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => handleFindExistingMatchesClick(createdJobNotice.id)}
                disabled={!hasPaidPlan}
                title={!hasPaidPlan ? "Available on Basic and Pro plans." : "Find candidates already in your pool"}
                className={`inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition ${
                  !hasPaidPlan
                    ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                    : "bg-emerald-800 text-white hover:bg-emerald-900"
                }`}
              >
                <Sparkles size={16} />
                Find Existing Matches
              </button>
              <button
                type="button"
                onClick={() => handleAddCandidatesClick(createdJobNotice.id)}
                disabled={!hasPaidPlan}
                title={!hasPaidPlan ? "Available on Basic and Pro plans." : "Add candidates to this job"}
                className={`inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border px-4 text-sm font-semibold transition ${
                  !hasPaidPlan
                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                    : "border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
                }`}
              >
                <Users size={16} />
                Add Candidates
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 p-5 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">
              {jobView === "archived" ? "Archived Jobs" : "All Jobs"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {jobView === "archived"
                ? "Review closed or filled roles. Restore a job before scanning more candidates."
                : "Open a job to review candidates, scan applicants, or search your candidate database."}
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
              {[
                { key: "active", label: "Active" },
                { key: "archived", label: "Archived" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setJobView(item.key)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                    jobView === item.key
                      ? "bg-white text-slate-950 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter jobs"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 md:w-64"
            />
          </div>
        </div>

        {fetching ? (
          <p className="text-sm text-slate-500">Loading jobs...</p>
        ) : filteredJobs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center">
            <Briefcase className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 text-sm font-medium text-slate-700">No jobs found</p>
            <p className="mt-1 text-sm text-slate-500">
              {jobView === "archived"
                ? "Archived jobs will appear here when roles are filled or closed."
                : "Create your first job to start scanning candidates."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredJobs.map((job) => (
              <article key={job.id} className="min-w-0 overflow-hidden rounded-lg border border-slate-200 p-4 transition hover:border-slate-300 hover:shadow-sm sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-slate-950 sm:text-lg">{job.name}</h3>
                    <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500 sm:mt-2 sm:line-clamp-3 sm:leading-6">
                      {job.description}
                    </p>
                  </div>

                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
                    <Users size={14} />
                    {job.total_candidates || 0}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div className="min-w-0 rounded-md bg-slate-50 p-2 sm:p-3">
                    <p className="truncate text-[11px] text-slate-500 sm:text-xs">
                      <span className="sm:hidden">CVs</span>
                      <span className="hidden sm:inline">Candidates</span>
                    </p>
                    <p className="mt-1 font-semibold text-slate-950">{job.total_candidates || 0}</p>
                  </div>
                  <div className="min-w-0 rounded-md bg-slate-50 p-2 sm:p-3">
                    <p className="truncate text-[11px] text-slate-500 sm:text-xs">
                      <span className="sm:hidden">Saved</span>
                      <span className="hidden sm:inline">Shortlisted</span>
                    </p>
                    <p className="mt-1 font-semibold text-slate-950">{job.shortlisted || 0}</p>
                  </div>
                  <div className="min-w-0 rounded-md bg-slate-50 p-2 sm:p-3">
                    <p className="truncate text-[11px] text-slate-500 sm:text-xs">Avg</p>
                    <p className="mt-1 font-semibold text-slate-950">{job.average_score || 0}%</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <button
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    className="inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg bg-emerald-800 px-3 text-xs font-semibold leading-tight text-white transition hover:bg-emerald-900 sm:px-4 sm:text-sm"
                  >
                    <Eye size={15} />
                    View Job
                  </button>

                  {jobView === "archived" ? (
                    <button
                      onClick={() => handleRestoreJob(job.id)}
                      disabled={updatingJobId === job.id}
                      className="inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold leading-tight text-emerald-800 transition hover:bg-emerald-50 disabled:opacity-50 sm:px-4 sm:text-sm xl:col-span-2"
                    >
                      <RotateCcw size={15} />
                      {updatingJobId === job.id ? "Restoring..." : "Restore Job"}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleFindExistingMatchesClick(job.id)}
                        disabled={!hasPaidPlan}
                        title={!hasPaidPlan ? "Available on Basic and Pro plans." : "Find candidates already in your pool"}
                        className={`inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg border bg-white px-3 text-center text-xs font-semibold leading-tight transition sm:px-4 sm:text-sm ${
                          !hasPaidPlan
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : "border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        }`}
                      >
                        <Search size={15} />
                        AI Pool Matches
                      </button>

                      <button
                        onClick={() => handleAddCandidatesClick(job.id)}
                        disabled={!hasPaidPlan}
                        title={!hasPaidPlan ? "Available on Basic and Pro plans." : "Add candidates to this job"}
                        className={`inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg border bg-white px-3 text-center text-xs font-semibold leading-tight transition sm:px-4 sm:text-sm ${
                          !hasPaidPlan
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                            : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                        }`}
                      >
                        <Users size={15} />
                        Add Candidates
                      </button>

                      <button
                        onClick={() => handleBulkScanClick(job.id)}
                        aria-disabled={bulkScanLocked}
                        className={`inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg border bg-white px-3 text-center text-xs font-semibold leading-tight transition sm:px-4 sm:text-sm ${
                          bulkScanLocked
                            ? "cursor-not-allowed border-slate-200 text-slate-400 hover:bg-white"
                            : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                        }`}
                      >
                        <FileStack size={15} />
                        Bulk Scan
                      </button>

                      <button
                        onClick={() => handleCriteriaClick(job)}
                        aria-disabled={(job.scan_count || 0) <= 0}
                        className={`inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg border bg-white px-3 text-center text-xs font-semibold leading-tight transition sm:px-4 sm:text-sm ${
                          (job.scan_count || 0) <= 0
                            ? "cursor-not-allowed border-slate-200 text-slate-400 hover:bg-white"
                            : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                        }`}
                      >
                        <Target size={15} />
                        Criteria
                      </button>

                      <button
                        onClick={() => handleArchiveJob(job.id)}
                        disabled={updatingJobId === job.id}
                        className="inline-flex min-h-[40px] min-w-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-center text-xs font-semibold leading-tight text-slate-700 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50 sm:px-4 sm:text-sm"
                      >
                        <Archive size={15} />
                        {updatingJobId === job.id ? "Archiving..." : "Archive"}
                      </button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );

  const rightPanel = (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="font-semibold text-slate-950">Workflow</h4>
      <div className="mt-4 space-y-4 text-sm text-slate-600">
        <p>Create a job once and use it as the anchor for candidate scans, shortlisting, and review.</p>
        <p>Bulk Scan and AI Talent Search remain available as standalone tools from the recruiter menu.</p>
      </div>
    </div>
  );

  if (loading || !user) return <p className="text-center mt-20">Loading...</p>;

  return (
    <DashboardLayout
      user={user}
      eyebrow="Job management"
      title="Jobs"
      subtitle="Create roles, review job-level candidate history, and continue with scan tools"
      leftPanel={leftPanel}
      mainContent={mainContent}
      rightPanel={rightPanel}
    />
  );
}
